// src/pages/LearningPathGraphPage.tsx — /paths/:pathId wrapper: Cubemate
// sidebar + the dark-themed PathGraph (same wrapping pattern as MLmatePage).
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import PathGraph from '../paths/PathGraph';
import { pathsApi } from '../services/api';
import { PathResponse } from '../types/paths';

const LearningPathGraphPage: React.FC = () => {
  const { pathId } = useParams<{ pathId: string }>();
  const navigate = useNavigate();
  const [path, setPath] = useState<PathResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!pathId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await pathsApi.getPath(pathId);
        if (!cancelled) setPath(res);
      } catch (err: any) {
        console.error('[PATHS] Failed to load path:', err);
        if (!cancelled) {
          setError(
            err?.response?.status === 404
              ? 'This path was not found — it may have been deleted.'
              : 'Could not load this path. Please try again.'
          );
        }
      }
    })();
    return () => { cancelled = true; };
  }, [pathId]);

  return (
    <div className="h-screen flex overflow-hidden">
      <Sidebar />
      <div className="flex-1 min-w-0 overflow-hidden">
        {error ? (
          <div className="h-full flex items-center justify-center bg-background">
            <div className="text-center space-y-3">
              <div className="text-3xl">🧭</div>
              <p className="text-sm text-foreground">{error}</p>
              <button
                onClick={() => navigate('/paths')}
                className="px-5 py-2 rounded-xl bg-gradient-to-r from-purple-500 to-blue-500 text-white text-sm font-semibold"
              >
                ← Back to my paths
              </button>
            </div>
          </div>
        ) : !path ? (
          <div className="h-full flex items-center justify-center bg-background">
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <span className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></span>
              Loading your path…
            </div>
          </div>
        ) : (
          <PathGraph path={path} onPathUpdate={setPath} />
        )}
      </div>
    </div>
  );
};

export default LearningPathGraphPage;
